import React, { useState } from "react"
import { Button, IconButton, Modal, Select } from "uxp/components"
import { IContextProvider } from "../../../uxp"
import jp from 'jsonpath'
import { replaceWithData } from "../../../util"
import { WidgetDesignerPillInput } from "../pill-input/PillInput"
import { IWDDesignModeProps } from "../common"
import { $L } from "../../../localize"
import './QueryBuilder.scss'
import classNames from "classnames"

export type IOperator = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'nin'

export interface IQueryCondition {
    Attribute: string,
    Operator: IOperator,
    Value: any,
}

export interface IQueryConditionGroup {
    IsEither: boolean,
    Conditions: IQueryCondition[],
    Groups: IQueryConditionGroup[]
}

export interface IConditionBuilderProps {
    uxpContext: IContextProvider,
    contextData: any,
    uiProps: any,
    designer: IWDDesignModeProps
    conditions: IQueryConditionGroup,
    onChangeConditions: (conditions: IQueryConditionGroup) => void
}

export const ConditionBuilder: React.FunctionComponent<IConditionBuilderProps> = (props) => {
    const { uxpContext, contextData, uiProps, designer, conditions, onChangeConditions } = props
    return <div className='uxp-wd-cb'>
        <ConditionGroup
            uxpContext={uxpContext}
            uiProps={uiProps}
            contextData={contextData}
            designer={designer}
            group={conditions}
            onChangeGroup={g => {
                onChangeConditions(g)
            }}
            index={null}
            isParent
            onRemoveGroup={() => { }}
        />
    </div>
}


export interface IConditionGroupProps {
    uxpContext: IContextProvider,
    contextData: any,
    uiProps: any,
    designer: IWDDesignModeProps
    group: IQueryConditionGroup,
    index: number,
    onChangeGroup: (group: IQueryConditionGroup) => void
    onRemoveGroup: () => void
    isParent?: boolean
}

export const ConditionGroup: React.FunctionComponent<IConditionGroupProps> = (props) => {
    let { group, onChangeGroup, uxpContext, uiProps, contextData, designer, isParent, onRemoveGroup } = props

    const itemCount = group.Conditions.length + group.Groups.length

    // Label shown at the start of each row: first row overall = "where",
    // every subsequent row = the group operator ("and" / "or").
    function rowLabel(overallIndex: number) {
        if (overallIndex === 0) return $L('widgetdesigner.props.where', 'where')
        return group.IsEither
            ? $L('widgetdesigner.props.or', 'or')
            : $L('widgetdesigner.props.and', 'and')
    }

    const groupCard = <div className={classNames('uxp-wd-cb__group', { 'uxp-wd-cb__group--parent': isParent })}>
        {itemCount > 1 &&
            <div className="uxp-wd-cb__group-hd">
                <span className="uxp-wd-cb__group-hd-text">
                    {group.IsEither
                        ? $L('widgetdesigner.props.any-conditions-true', 'Any of the following conditions are true')
                        : $L('widgetdesigner.props.all-conditions-true', 'All of the following conditions are true')}
                </span>
                <Select
                    selected={group.IsEither ? 'or' : 'and'}
                    onChange={s => {
                        jp.apply(group, '$.IsEither', () => (s == 'or'))
                        onChangeGroup(group)
                    }}
                    options={[
                        { label: $L('widgetdesigner.props.and', 'and'), value: 'and' },
                        { label: $L('widgetdesigner.props.or', 'or'), value: 'or' },
                    ]}
                    className="uxp-wd-cb__group-op"
                />
            </div>
        }

        <div className="uxp-wd-cb__group-body">
            {itemCount === 0 &&
                <div className="uxp-wd-cb__empty">
                    {$L('widgetdesigner.props.no-conditions', 'No conditions')}
                </div>
            }
            {
                group?.Conditions?.map((condition, i) => {
                    return <div className="uxp-wd-cb__row" key={'c' + i}>
                        <div className="uxp-wd-cb__row-lbl">{rowLabel(i)}</div>
                        <Condition
                            uxpContext={uxpContext}
                            contextData={contextData}
                            uiProps={uiProps}
                            designer={designer}
                            condition={condition}
                            index={i}
                            onChangeCondition={(updated) => {
                                group.Conditions[i] = updated
                                onChangeGroup(group)
                            }}
                            onRemoveCondition={() => {
                                group.Conditions.splice(i, 1)
                                onChangeGroup(group)
                            }}
                        />
                    </div>
                })
            }
            {
                group?.Groups?.map((cGroup, i) => {
                    return <div className="uxp-wd-cb__row uxp-wd-cb__row--group" key={'g' + i}>
                        <div className="uxp-wd-cb__row-lbl">{rowLabel(group.Conditions.length + i)}</div>
                        <div className="uxp-wd-cb__row-fields">
                            <ConditionGroup
                                uxpContext={uxpContext}
                                contextData={contextData}
                                uiProps={uiProps}
                                designer={designer}
                                group={cGroup}
                                index={i}
                                onChangeGroup={(updated) => {
                                    group.Groups[i] = updated
                                    onChangeGroup(group)
                                }}
                                onRemoveGroup={() => {
                                    group.Groups.splice(i, 1)
                                    onChangeGroup(group)
                                }}
                            />
                        </div>
                        <div className="uxp-wd-cb__row-delete">
                            <IconButton type="delete" variant="danger" size="medium" onClick={() => {
                                group.Groups.splice(i, 1)
                                onChangeGroup(group)
                            }} />
                        </div>
                    </div>
                })
            }
        </div>

        <div className="uxp-wd-cb__group-footer">
            <div className="uxp-wd-cb__add-btn"
                onClick={() => {
                    group.Conditions.push({ Attribute: '', Operator: 'eq', Value: '' })
                    onChangeGroup(group)
                }}
            >
                {$L('widgetdesigner.props.add-condition', '+ Add condition')}
            </div>
            <div className="uxp-wd-cb__add-btn"
                onClick={() => {
                    group.Groups.push({ IsEither: true, Conditions: [], Groups: [] })
                    onChangeGroup(group)
                }}
            >
                {$L('widgetdesigner.props.add-group', '+ Add group')}
            </div>
        </div>
    </div>

    return groupCard
}

export interface IConditionProps {
    uxpContext: IContextProvider,
    contextData: any,
    uiProps: any,
    designer: IWDDesignModeProps
    condition: IQueryCondition,
    index: number,
    onChangeCondition: (condition: IQueryCondition) => void
    onRemoveCondition: () => void
}

export const Condition: React.FunctionComponent<IConditionProps> = (props) => {
    let { condition, onChangeCondition, uxpContext, uiProps, contextData, designer, onRemoveCondition } = props

    return <>
        <div className="uxp-wd-cb__row-fields">
            <div className="uxp-wd-cb__field uxp-wd-cb__field--attr">
                <WidgetDesignerPillInput
                    value={condition.Attribute}
                    onChange={s => {
                        jp.apply(condition, '$.Attribute', () => s)
                        onChangeCondition(condition)
                    }}
                    contextData={contextData}
                />
            </div>

            <div className="uxp-wd-cb__field uxp-wd-cb__field--op">
                <Select
                    selected={condition.Operator}
                    onChange={(s) => {
                        jp.apply(condition, '$.Operator', () => s)
                        onChangeCondition(condition)
                    }}
                    options={[
                        { label: 'Equals', value: 'eq' },
                        { label: 'Not Equals', value: 'ne' },
                        { label: 'Greater than', value: 'gt' },
                        { label: 'Greater than or equals', value: 'gte' },
                        { label: 'Less than', value: 'lt' },
                        { label: 'Less than or equals', value: 'lte' },
                        { label: 'Is one of', value: 'in' },
                        { label: 'Is not one of', value: 'nin' },
                    ]}
                />
            </div>

            <div className="uxp-wd-cb__field uxp-wd-cb__field--val">
                <WidgetDesignerPillInput
                    value={condition.Value}
                    onChange={s => {
                        jp.apply(condition, '$.Value', () => s)
                        onChangeCondition(condition)
                    }}
                    contextData={contextData}
                />
            </div>
        </div>

        <div className="uxp-wd-cb__row-delete">
            <IconButton type="delete" variant="danger" size="medium" onClick={onRemoveCondition} />
        </div>
    </>
}

export const QueryBuilderModal: React.FunctionComponent<IConditionBuilderProps & { title?: string, show?: boolean, onClose?: () => void, hideTrigger?: boolean }> = (props) => {
    const { title, show: showProp, onClose: onCloseProp, hideTrigger, ...rest } = props

    const [internalShow, setInternalShow] = useState(false)
    const isControlled = showProp !== undefined
    const show = isControlled ? showProp : internalShow

    const hasConditions = rest?.conditions?.Groups?.length > 0 || rest?.conditions?.Conditions?.length > 0

    const close = () => { if (isControlled) onCloseProp?.(); else setInternalShow(false) }

    return <div className="uxp-conditions-editor-modal-container">
        {!hideTrigger &&
            <Button
                title={hasConditions ? 'Edit Conditions' : 'Add Conditions'}
                onClick={() => setInternalShow(true)}
                active={hasConditions}
                className="uxp-conditions-editor-button"
            />
        }

        <Modal
            show={show}
            onClose={close}
            title={title || 'Condition Builder'}
            className="uxp-conditions-editor-modal"
        >
            <ConditionBuilder {...rest} />
        </Modal>
    </div>
}

export function evaluateConditions(conditions: IQueryConditionGroup, contextData: any): boolean {

    const { IsEither, Conditions, Groups } = conditions

    if ((Conditions.length + Groups.length) == 0) return true

    let isTrue = IsEither ? false : true
    for (const c of Conditions) {
        const isConditionTrue = evaluateCondition(c, contextData)
        if (IsEither && isConditionTrue) { isTrue = true; break; }
        if (!IsEither && !isConditionTrue) { isTrue = false; break; }
    }

    for (const g of Groups) {
        const isGroupTrue = evaluateConditions(g, contextData)
        if (IsEither && isGroupTrue) { isTrue = true; break; }
        if (!IsEither && !isGroupTrue) { isTrue = false; break; }
    }
    return isTrue
}

export function evaluateCondition(condition: IQueryCondition, contextData: any): boolean {
    const { Attribute, Operator, Value } = condition
    const parsedAttribute = replaceWithData(Attribute?.trim(), contextData)
    const parsedValue = Operator == 'in' ? (Value as string)?.trim()?.split(",").filter(s => !!s).map(s => replaceWithData(s, contextData)) : replaceWithData(Value?.trim(), contextData)


    switch (Operator) {
        case 'eq':
            return (parsedAttribute == parsedValue)
        case 'ne':
            return (parsedAttribute != parsedValue)
        case 'gt':
            return (parsedAttribute > parsedValue)
        case 'gte':
            return (parsedAttribute >= parsedValue)
        case 'lt':
            return (parsedAttribute < parsedValue)
        case 'lte':
            return (parsedAttribute <= parsedValue)
        case 'in':
            return (parsedValue as string[]).includes(parsedAttribute)
        case 'nin':
            return !(parsedValue as string[]).includes(parsedAttribute)
        default:
            return true
    }
}